import { useEffect, useState } from "react";
import { BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { fmtDate } from "@/lib/community";
import { resolveImage } from "@/lib/siteDefaults";
import { useAdminData, adminAct, Badge, Panel, btnDark, btnDanger, btnLine } from "./shared";

function Matches({ orders }) {
  if (!orders?.length) return <span className="text-xs text-[#7A6A62]">No qualifying paid order found for this email</span>;
  return (
    <span className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="prive-match">
      <BadgeCheck className="w-3.5 h-3.5" /> {orders.length} qualifying order{orders.length > 1 ? "s" : ""}: {orders.map((o) => `#${o.order_ref}`).join(", ")}
    </span>
  );
}

function QualifyingProducts({ selected, onSaved }) {
  const [products] = useAdminData("/admin/products");
  const [ids, setIds] = useState(selected);
  useEffect(() => setIds(selected), [selected]);
  const toggle = (id, on) => setIds((x) => (on ? [...x, id] : x.filter((i) => i !== id)));
  const save = () => adminAct(() => api.put("/admin/community/prive/settings", { qualifying_product_ids: ids }), "Qualifying sets saved").then(onSaved);
  return (
    <Panel title="Qualifying Jenova sets" testId="prive-qualifying-panel" action={<button className={btnDark} onClick={save} data-testid="prive-qualifying-save">Save</button>}>
      <p className="text-sm text-[#7A6A62] mb-4">Tick the products that qualify a purchaser for Privé. Paid orders containing them are auto-matched to members by email — you still approve each one.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(products || []).map((p) => (
          <label key={p.id} className="flex items-center gap-3 border border-[#E8DCCB] rounded-lg p-3 text-sm" data-testid={`prive-product-${p.id}`}>
            {p.images?.[0] && <img src={resolveImage(p.images[0])} alt="" className="w-10 h-12 object-cover rounded" />}
            <span className="flex-1">{p.name}</span>
            <Switch checked={ids.includes(p.id)} onCheckedChange={(v) => toggle(p.id, v)} data-testid={`prive-product-switch-${p.id}`} />
          </label>
        ))}
      </div>
    </Panel>
  );
}

function TicketDiscount({ value, onSaved }) {
  const [pct, setPct] = useState(value);
  useEffect(() => setPct(value), [value]);
  const save = () => adminAct(() => api.put("/admin/community/prive/settings", { ticket_discount_percent: Number(pct) || 0 }), "Privé ticket discount saved").then(onSaved);
  return (
    <Panel title="Privé ticket discount" testId="prive-discount-panel">
      <p className="text-sm text-[#7A6A62] mb-4">Approved Privé members automatically pay this much less on every paid experience. Set 0 to turn it off.</p>
      <div className="flex items-center gap-3">
        <input type="number" min={0} max={90} className="w-24 border border-[#E8DCCB] rounded-md px-3 py-2 text-sm" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="prive-discount-input" />
        <span className="text-sm">% off</span>
        <button className={btnDark} onClick={save} data-testid="prive-discount-save">Save</button>
      </div>
    </Panel>
  );
}

function ChannelLink({ value, sent, onSaved }) {
  const [url, setUrl] = useState(value || "");
  useEffect(() => setUrl(value || ""), [value]);
  const save = () => adminAct(() => api.put("/admin/community/prive/settings", { channel_url: url.trim() || null }), "Privé channel link saved").then(onSaved);
  return (
    <Panel title="Privé Instagram channel" testId="prive-channel-panel">
      <p className="text-sm text-[#7A6A62] mb-4">Every new paid order that includes a qualifying set automatically gets an email with this link (within about 15 minutes of purchase, once per order). {sent ? `${sent} sent so far.` : ""}</p>
      <div className="flex flex-wrap items-center gap-3">
        <input className="flex-1 min-w-[260px] border border-[#E8DCCB] rounded-md px-3 py-2 text-sm" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.instagram.com/channel/…" data-testid="prive-channel-input" />
        <button className={btnDark} onClick={save} data-testid="prive-channel-save">Save</button>
      </div>
    </Panel>
  );
}

const DT = { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" };

function EarlyAccessPanel() {
  const [rows, reload] = useAdminData("/admin/community/early-access");
  const [products] = useAdminData("/admin/products");
  const [f, setF] = useState({ product_id: "", hours: 24, starts_at: "", notify: true });
  const start = async () => {
    if (!f.product_id) return window.alert("Choose a product");
    const r = await adminAct(() => api.post("/admin/community/early-access", { ...f, hours: Number(f.hours) || 24, starts_at: f.starts_at ? new Date(f.starts_at).toISOString() : null }),
      f.starts_at ? "Early access scheduled" : "Early access is live — Privé members are being emailed");
    if (r) { setF({ ...f, product_id: "", starts_at: "" }); reload(); }
  };
  const end = (w) => window.confirm(`End early access for ${w.product_name} now? Everyone will be able to buy it.`) && adminAct(() => api.delete(`/admin/community/early-access/${w.id}`), "Early access ended").then(reload);
  const now = new Date();
  const state = (w) => (w.cancelled ? "ended" : new Date(w.starts_at) > now ? "scheduled" : new Date(w.ends_at) > now ? "live" : "ended");
  return (
    <Panel title="Privé Early Access" testId="early-access-panel">
      <p className="text-sm text-[#7A6A62] mb-4">For a new release or a restock: everyone can see the product, but only signed-in Privé members can buy it until early access ends. For a restock, start early access first, then update the stock — Privé members on the "back in stock" list are emailed right away and everyone else when early access ends.</p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-[#7A6A62]">Product
          <select className="block mt-1 border border-[#E8DCCB] rounded-md px-3 py-2 text-sm min-w-[220px]" value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })} data-testid="early-access-product-select">
            <option value="">Choose…</option>
            {(products || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label className="text-xs text-[#7A6A62]">Hours
          <input type="number" min={1} max={168} className="block mt-1 w-20 border border-[#E8DCCB] rounded-md px-3 py-2 text-sm" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} data-testid="early-access-hours-input" />
        </label>
        <label className="text-xs text-[#7A6A62]">Start (leave empty for now)
          <input type="datetime-local" className="block mt-1 border border-[#E8DCCB] rounded-md px-3 py-2 text-sm" value={f.starts_at} onChange={(e) => setF({ ...f, starts_at: e.target.value })} data-testid="early-access-start-input" />
        </label>
        <label className="flex items-center gap-2 text-sm pb-2"><Switch checked={f.notify} onCheckedChange={(v) => setF({ ...f, notify: v })} data-testid="early-access-notify-switch" /> Email Privé members</label>
        <button className={btnDark} onClick={start} data-testid="early-access-start-btn">Start early access</button>
      </div>
      <div className="mt-5 divide-y divide-[#F0E8DD]">
        {(rows || []).map((w) => (
          <div key={w.id} className="py-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`early-access-row-${w.id}`}>
            <span className="flex-1 min-w-[200px]">{w.product_name}</span>
            <span className="text-xs text-[#7A6A62]">{fmtDate(w.starts_at, DT)} → {fmtDate(w.ends_at, DT)}{w.notified_count != null ? ` · emailed ${w.notified_count}` : ""}</span>
            <Badge s={state(w)} />
            {state(w) !== "ended" && <button className={btnDanger} onClick={() => end(w)} data-testid={`early-access-end-${w.id}`}>End now</button>}
          </div>
        ))}
      </div>
    </Panel>
  );
}

export default function PriveTab() {
  const [data, reload] = useAdminData("/admin/community/prive");
  const [sugg, reloadSugg] = useAdminData("/admin/community/prive/suggestions");
  const setStatus = async (m, prive_status) => {
    if (prive_status !== "approved" && !window.confirm(`Set ${m.email} Privé access to "${prive_status}"?`)) return;
    await adminAct(() => api.put(`/admin/community/members/${m.user_id}`, { prive_status }), prive_status === "approved" ? "Privé approved — welcome email sent" : "Updated");
    reload();
    reloadSugg();
  };
  if (!data) return null;
  const pending = data.members.filter((m) => m.prive_status === "pending");
  const others = data.members.filter((m) => m.prive_status !== "pending");
  const row = (m, actions) => (
    <div key={m.user_id} className="py-4 flex flex-wrap items-center gap-4 border-b border-[#F0E8DD]" data-testid={`prive-row-${m.user_id}`}>
      <div className="flex-1 min-w-[220px]">
        <p className="text-sm">{m.name || "—"} <span className="text-[#7A6A62]">· {m.email}</span></p>
        <div className="mt-1"><Matches orders={m.matched_orders} /></div>
        {m.prive_request?.order_reference && <p className="text-xs text-[#7A6A62] mt-1">Order given: {m.prive_request.order_reference}{m.prive_request.note ? ` — “${m.prive_request.note}”` : ""}</p>}
        {m.prive_requested_at && <p className="text-[11px] text-[#7A6A62] mt-0.5">Requested {fmtDate(m.prive_requested_at)}</p>}
      </div>
      <Badge s={m.prive_status} />
      <div className="flex gap-1.5">{actions}</div>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="admin-prive-tab">
      <QualifyingProducts selected={data.qualifying_product_ids} onSaved={() => { reload(); reloadSugg(); }} />
      <TicketDiscount value={data.ticket_discount_percent} onSaved={reload} />
      <ChannelLink value={data.channel_url} sent={data.channel_emails_sent} onSaved={reload} />
      <EarlyAccessPanel />
      <Panel title={`Verification requests (${pending.length})`}>
        {pending.map((m) => row(m, <>
          <button className={btnDark} onClick={() => setStatus(m, "approved")} data-testid={`prive-approve-${m.user_id}`}>Approve</button>
          <button className={btnDanger} onClick={() => setStatus(m, "rejected")} data-testid={`prive-reject-${m.user_id}`}>Decline</button>
        </>))}
        {!pending.length && <p className="text-sm text-[#7A6A62]">No pending requests.</p>}
      </Panel>
      <Panel title="Suggested purchasers" testId="prive-suggestions">
        <p className="text-sm text-[#7A6A62] mb-2">Signed-in customers who bought a qualifying set but aren't Privé yet.</p>
        {(sugg || []).map((m) => row(m, <button className={btnDark} onClick={() => setStatus(m, "approved")} data-testid={`prive-grant-${m.user_id}`}>Grant Privé</button>))}
        {sugg && !sugg.length && <p className="text-sm text-[#7A6A62]">No suggestions right now.</p>}
      </Panel>
      <Panel title="All Privé members">
        {others.map((m) => row(m, <>
          {m.prive_status !== "approved" && <button className={btnLine} onClick={() => setStatus(m, "approved")} data-testid={`prive-reinstate-${m.user_id}`}>Approve</button>}
          {m.prive_status === "approved" && <button className={btnLine} onClick={() => setStatus(m, "suspended")} data-testid={`prive-suspend-${m.user_id}`}>Suspend</button>}
          {m.prive_status !== "revoked" && <button className={btnDanger} onClick={() => setStatus(m, "revoked")} data-testid={`prive-revoke-${m.user_id}`}>Revoke</button>}
        </>))}
        {!others.length && <p className="text-sm text-[#7A6A62]">No Privé members yet.</p>}
      </Panel>
    </div>
  );
}
